import { initializeApp } from 'firebase/app'
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  connectAuthEmulator,
} from 'firebase/auth'
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  connectFirestoreEmulator,
} from 'firebase/firestore'
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  connectStorageEmulator,
} from 'firebase/storage'
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions'

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyApECZJ-C05ul1V4R6PeyI223C7of6a0uM",
  authDomain: "dlbcclarksville-5988b.firebaseapp.com",
  projectId: "dlbcclarksville-5988b",
  storageBucket: "dlbcclarksville-5988b.firebasestorage.app",
  messagingSenderId: "280703902403",
  appId: "1:280703902403:web:143430519d06b17bd0b876",
  measurementId: "G-50B55Y2NWS"
}

// Check if Firebase is configured
const isFirebaseConfigured = firebaseConfig.apiKey && firebaseConfig.projectId

// Initialize Firebase only if configured
let app = null
let auth = null
let db = null
let storage = null
let functions = null

if (isFirebaseConfigured) {
  try {
    app = initializeApp(firebaseConfig)
    auth = getAuth(app)
    db = getFirestore(app)
    storage = getStorage(app)
    functions = getFunctions(app)

    if (import.meta.env.VITE_USE_EMULATORS === 'true') {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
      connectFirestoreEmulator(db, '127.0.0.1', 8080)
      connectStorageEmulator(storage, '127.0.0.1', 9199)
      connectFunctionsEmulator(functions, '127.0.0.1', 5001)
    }
  } catch (error) {
    console.warn('Firebase initialization error:', error)
  }
}

export { auth, db, storage, functions }

// Callable Cloud Functions
export const callFunction = (name, data) => {
  if (!functions) {
    return Promise.reject(new Error('Firebase Functions not configured'))
  }
  return httpsCallable(functions, name)(data).then((res) => res.data)
}

// Auth functions
export const loginWithEmail = (email, password) => {
  if (!auth) {
    console.warn('Firebase Auth not configured')
    return Promise.reject(new Error('Firebase not configured'))
  }
  return signInWithEmailAndPassword(auth, email, password)
}

export const logout = () => {
  if (!auth) return Promise.resolve()
  return signOut(auth)
}

export const registerWithEmail = (email, password) => {
  if (!auth) {
    console.warn('Firebase Auth not configured')
    return Promise.reject(new Error('Firebase not configured'))
  }
  return createUserWithEmailAndPassword(auth, email, password)
}

export const resetPassword = (email) => {
  if (!auth) {
    return Promise.reject(new Error('Firebase not configured'))
  }
  return sendPasswordResetEmail(auth, email)
}

export const onAuthChange = (callback) => {
  if (!auth) {
    // Call with null user immediately and return a no-op unsubscribe
    setTimeout(() => callback(null), 0)
    return () => {}
  }
  return onAuthStateChanged(auth, callback)
}

// Firestore helpers
export const getCollection = async (collectionName, queryConstraints = []) => {
  if (!db) {
    console.warn('Firestore not configured, returning empty array')
    return []
  }
  const collectionRef = collection(db, collectionName)
  const q = queryConstraints.length > 0
    ? query(collectionRef, ...queryConstraints)
    : collectionRef
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

export const getDocument = async (collectionName, docId) => {
  if (!db) return null
  const docRef = doc(db, collectionName, docId)
  const snapshot = await getDoc(docRef)
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null
}

export const addDocument = async (collectionName, data) => {
  if (!db) {
    console.warn('Firestore not configured')
    return 'mock-id-' + Date.now()
  }
  const collectionRef = collection(db, collectionName)
  const docRef = await addDoc(collectionRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return docRef.id
}

export const setDocument = async (collectionName, docId, data, merge = false) => {
  if (!db) {
    console.warn('Firestore not configured')
    return
  }
  const docRef = doc(db, collectionName, docId)
  await setDoc(docRef, {
    ...data,
    createdAt: data.createdAt ?? serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge })
}

export const updateDocument = async (collectionName, docId, data) => {
  if (!db) {
    console.warn('Firestore not configured')
    return
  }
  const docRef = doc(db, collectionName, docId)
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  })
}

export const deleteDocument = async (collectionName, docId) => {
  if (!db) return
  const docRef = doc(db, collectionName, docId)
  await deleteDoc(docRef)
}

export const subscribeToCollection = (collectionName, callback, queryConstraints = []) => {
  if (!db) {
    // Return empty array immediately and a no-op unsubscribe
    setTimeout(() => callback([]), 0)
    return () => {}
  }
  const collectionRef = collection(db, collectionName)
  const q = queryConstraints.length > 0
    ? query(collectionRef, ...queryConstraints)
    : collectionRef
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    callback(data)
  }, (error) => {
    console.warn('Firestore subscription error:', error)
    callback([])
  })
}

export const subscribeToDocument = (collectionName, docId, callback) => {
  if (!db) {
    setTimeout(() => callback(null), 0)
    return () => {}
  }
  const docRef = doc(db, collectionName, docId)
  return onSnapshot(docRef, (snapshot) => {
    callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null)
  }, (error) => {
    console.warn('Firestore subscription error:', error)
    callback(null)
  })
}

// Storage functions
export const uploadFile = async (file, path) => {
  if (!storage) {
    console.warn('Firebase Storage not configured')
    // Return a mock URL for demo purposes
    return URL.createObjectURL(file)
  }
  const storageRef = ref(storage, path)
  const snapshot = await uploadBytes(storageRef, file)
  const url = await getDownloadURL(snapshot.ref)
  return url
}

export const deleteFile = async (path) => {
  if (!storage) return
  const storageRef = ref(storage, path)
  await deleteObject(storageRef)
}

export const listFiles = async (path) => {
  if (!storage) return []
  const storageRef = ref(storage, path)
  const result = await listAll(storageRef)
  const files = await Promise.all(
    result.items.map(async (item) => ({
      name: item.name,
      fullPath: item.fullPath,
      url: await getDownloadURL(item),
    }))
  )
  return files
}

// Export Firestore utilities
export {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
}
